/**
 * SAB-930: the nfl_leagues.status vocabulary, and which of its values mean "underway".
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 *
 * landingRoute.ts computed its first landing signal as:
 *
 *     hasActiveNfl = nflLeagues.some((l) => l.status === 'active');
 *
 * **Nothing has ever written 'active' to nfl_leagues.status.** The complete writer set is
 * the seven values below, swept across every edge function AND live pg_proc and now
 * enforced by the SAB-928 CHECK constraint -- which does not contain 'active'. So the
 * signal was permanently false and landing rule 1 never fired for anyone.
 *
 * It was not a dead term inside a wider filter; it was the SOLE condition producing the
 * signal. The existing suite could not catch it either, because it tests pickLandingRoute
 * (the pure chain, which was always correct) and the defect is in the signal DERIVATION.
 * Its own header even records that rule 1 "has ZERO live prod users today" and attributes
 * that to prod data rather than to a predicate that cannot be true.
 *
 * ── WHY A SHARED CONSTANT RATHER THAN A ONE-WORD FIX ────────────────────────
 *
 * The obvious repair is 'active' -> 'in_season'. That is still wrong: a user whose DRAFT
 * IS RUNNING would keep losing to an in-season MLB team and be dropped into the wrong
 * sport -- the worst possible moment to misroute someone, because a draft is timed and
 * autopick takes their pick while they navigate back.
 *
 * "Underway" means started and not finished. That is four of the seven values.
 *
 * ── THE PARTITION IS THE POINT ──────────────────────────────────────────────
 *
 * LIVE and NOT_LIVE are asserted to be disjoint and to cover the vocabulary exactly. So a
 * status added to nfl_leagues (and to the SAB-928 constraint) without being classified
 * here FAILS THE TEST rather than silently defaulting to "not live" and quietly changing
 * where users land. That is the whole reason this is a partition and not a single Set:
 * the next status forces a decision instead of falling through.
 */

/**
 * Every value nfl_leagues.status may hold. Must stay in lockstep with the SAB-928
 * constraint `nfl_leagues_status_vocabulary`; the test pins them against each other.
 */
export const NFL_LEAGUE_STATUSES = [
  'pre_draft',
  'drafting',
  'draft_completed',
  'in_season',
  'playoffs',
  'completed',
  'cancelled',
] as const;

export type NflLeagueStatus = (typeof NFL_LEAGUE_STATUSES)[number];

/**
 * Underway: the draft has started and the season has not finished.
 *
 * `draft_completed` is included deliberately. It is the window between the last pick and
 * schedule generation (a league sits there until generate-nfl-schedule runs), and a user
 * in it is very much mid-league -- sending them to another sport there would be the same
 * mistake as sending them away mid-draft.
 */
export const NFL_LIVE_STATUSES: ReadonlySet<string> = new Set<NflLeagueStatus>([
  'drafting',
  'draft_completed',
  'in_season',
  'playoffs',
]);

/**
 * Not underway: not started (`pre_draft`), or over (`completed`, `cancelled`).
 *
 * These still reach the NFL shell via landing rule 3 -- membership alone routes there.
 * The only thing this partition governs is what BEATS an in-season MLB team.
 */
export const NFL_NOT_LIVE_STATUSES: ReadonlySet<string> = new Set<NflLeagueStatus>([
  'pre_draft',
  'completed',
  'cancelled',
]);

/**
 * Is this league underway?
 *
 * Unknown values (including null/undefined, and anything the constraint would now reject)
 * are NOT live. Falling back to "not live" is the safe direction: it can only send a user
 * to the NFL shell one rule later via rule 3, never to the wrong sport.
 */
export function isNflLeagueLive(status: string | null | undefined): boolean {
  return status != null && NFL_LIVE_STATUSES.has(status);
}
